import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatSelect } from '@angular/material/select';
import { createFormControl, DestroyableBase, TypedFormControl } from 'osn-shared';
import { Observable, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';
import { deepEqual } from '../../functions/deepEqual';
import { AlwaysError } from '../../../core/services/always-error.service';

export interface MultiSelectOption {
  id: string;
  label: string;
}

@Component({
  selector: 'osn-cxb-multi-select',
  templateUrl: './multi-select.component.html',
  styleUrls: ['./multi-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultiSelectComponent),
      multi: true,
    },
  ],
})
export class MultiSelectComponent extends DestroyableBase implements OnInit, OnChanges, AfterViewInit, ControlValueAccessor {
  @ViewChild('multiSelect', { static: true }) public multiSelect: MatSelect;
  @Input() public allowMultiLine = false;
  @Input() public typeFirstVersion = false;
  @Input() public isFixedWidth = false;

  get options(): MultiSelectOption[] {
    return this._options;
  }
  @Input() public set options(value: MultiSelectOption[]) {
    this._options = [this.allOption, ...value];
  }
  @Input() public placeholder: string;
  @Input() public filterNoEntriesFoundLabel = 'No entries found';
  @Input() public filterPlaceholderLabel = 'Filter entries...';
  @Input() public isLoading$: Observable<boolean>;
  @Input() public hintLabel: string;
  @Input() public errorLabel: string;
  @Input() public label: string;
  @Input() public emitValueOnRegisterOnChange = false;
  @Input() public emitEntireOption = false;
  @Input() public hasFixedOptions = true;
  // eslint-disable-next-line @angular-eslint/no-input-rename
  @Input('data-testid') public testId = 'multi-select';

  public propagateChange: Function = () => {};
  public propagateTouch: Function = () => {};

  public multiCtrl: TypedFormControl<MultiSelectOption[]>;
  public multiFilterCtrl: TypedFormControl<string> = createFormControl('');
  public filteredOptions$: ReplaySubject<MultiSelectOption[]> = new ReplaySubject<MultiSelectOption[]>(1);
  private _options: MultiSelectOption[];
  private isAllOptionSelected = false;
  private readonly allOption: MultiSelectOption = { label: 'All', id: 'all' };

  public get setSelectorTrigger(): string {
    return this.multiCtrl?.value.find((option) => option.id === this.allOption.id)
      ? 'All'
      : this.multiCtrl.value.map((option) => option.label).join(', ');
  }

  constructor(public readonly alwaysError: AlwaysError) {
    super();
  }

  public ngOnInit() {
    this.multiCtrl = createFormControl<MultiSelectOption[]>([]);
    this.filteredOptions$.next(this.typeFirstVersion ? [] : this.options?.slice() || []);
    this.multiCtrl.valueChanges
      .pipe(
        takeUntil(this.onDestroy$),
        distinctUntilChanged((a, b) => deepEqual(a, b)),
        map((options) => options.filter((option) => option.id !== this.allOption.id))
      )
      .subscribe((selectedOptions: MultiSelectOption[]) => {
        if (this.emitEntireOption) {
          this.propagateChange?.([...selectedOptions]);
        } else {
          this.propagateChange?.(selectedOptions.map((option) => option.id));
        }
      });

    this.multiFilterCtrl.valueChanges.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.filterMultiOptions();
    });
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.options?.currentValue?.length) {
      return;
    }
    this.filteredOptions$.next(this.typeFirstVersion ? [] : changes.options.currentValue);
  }

  public ngAfterViewInit() {
    this.setInitialValue();
  }

  public writeValue(values: string[] | MultiSelectOption[]): void {
    const ids = values?.length > 0 ? values.map((v) => v.id || v) : values;
    if (!this.options?.length || !ids?.length) {
      this.multiCtrl.setValue([]);
    } else {
      const initialOptions = ids.reduce((options: MultiSelectOption[], id: string) => {
        const option = this.options.find((o) => o.id === id);
        return option ? [...options, option] : options;
      }, []);
      if (initialOptions.length === this.options.length - 1) {
        initialOptions.push(this.allOption);
        this.isAllOptionSelected = true;
      }
      this.multiCtrl.setValue(initialOptions);
    }

    this.filterMultiOptions();
  }

  public registerOnChange(fn: any): void {
    this.propagateChange = fn;

    if (this.emitValueOnRegisterOnChange) {
      const options = (this.multiCtrl.value as MultiSelectOption[]).filter((option) => option.id !== this.allOption.id);
      if (this.emitEntireOption) {
        this.propagateChange?.(options);
      } else {
        this.propagateChange?.(options.map((it) => it.id));
      }
    }
  }

  public registerOnTouched(fn: any): void {
    this.propagateTouch = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.multiCtrl.disable();
    } else {
      this.multiCtrl.enable();
    }
  }

  public compareFunction(option1: MultiSelectOption, option2: MultiSelectOption): boolean {
    return option1.id === option2.id;
  }

  public onSelectionChange(selectedOptions: MultiSelectOption[]): void {
    if (selectedOptions.find((option) => option.id === this.allOption.id)) {
      if (!this.isAllOptionSelected) {
        this.isAllOptionSelected = true;
        this.multiCtrl.setValue(this.options);
      } else {
        this.isAllOptionSelected = false;
        this.multiCtrl.setValue(selectedOptions.filter((it) => it.id !== this.allOption.id));
      }
    } else {
      if (this.isAllOptionSelected && this.hasFixedOptions) {
        this.isAllOptionSelected = false;
        this.multiCtrl.setValue([]);
      } else {
        if (selectedOptions.length === this.options.length - 1) {
          this.isAllOptionSelected = true;
          this.multiCtrl.setValue([this.allOption, ...selectedOptions]);
        } else {
          this.multiCtrl.setValue(selectedOptions);
        }
      }
    }
  }

  private setInitialValue() {
    this.filteredOptions$.pipe(take(1), takeUntil(this.onDestroy$)).subscribe(() => {
      this.multiSelect.compareWith = (a: MultiSelectOption, b: MultiSelectOption) => a && b && a.id === b.id;
    });
  }

  private filterMultiOptions() {
    if (!this.options) {
      return;
    }
    let search = this.multiFilterCtrl.value;

    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (!search || (this.typeFirstVersion && search.length < 3)) {
      this.filteredOptions$.next(this.typeFirstVersion ? this.multiCtrl.value : this.options?.slice() || []);
      return;
    } else {
      search = search.toLowerCase();
    }
    this.filteredOptions$.next(this.options.filter((option) => option.label.toLowerCase().indexOf(search) > -1));
  }
}
